import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { ref, get, update } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { firestore, database, functions } from '../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { computeAndStoreSignals } from './leadSignalEngine';
import useResearchAccess from '../hooks/useResearchAccess';
import {
  IG_RESEARCH_SETTINGS_PATH,
  languageFromIgSettings,
  buildIgResearchDescriptionBlock,
} from './instagramResearchLanguage';
import { recordCompanyRecord } from '../shared/recordIntelligenceLog';
import './InstagramResearchModal.css';

const IG_RESEARCH_CREDIT_COST = 3;

function extractHandle(raw) {
  if (!raw) return '';
  return raw
    .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
    .replace(/^@/, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')
    .trim();
}

function formatNumber(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString();
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function postFingerprint(p) {
  return `${p.created_at || ''}::${(p.text || '').substring(0, 80)}`;
}

function mergePosts(oldPosts, newPosts) {
  const oldKeys = new Set((oldPosts || []).map(postFingerprint));
  const merged = [];
  const newCount = { value: 0 };

  for (const p of newPosts) {
    const fp = postFingerprint(p);
    const isNew = !oldKeys.has(fp);
    if (isNew) newCount.value++;
    merged.push({ ...p, _isNew: isNew });
  }

  const newKeys = new Set(newPosts.map(postFingerprint));
  for (const p of (oldPosts || [])) {
    if (!newKeys.has(postFingerprint(p))) {
      merged.push({ ...p, _isOld: true });
    }
  }

  merged.sort((a, b) => {
    const da = a.created_at ? new Date(a.created_at) : new Date(0);
    const db = b.created_at ? new Date(b.created_at) : new Date(0);
    return db - da;
  });

  return { merged, newCount: newCount.value };
}

const InstagramResearchModal = ({ isOpen, onClose, contactData, userId }) => {
  const { research, researchLoading } = useResearchAccess();
  const locked = !researchLoading && research.hasInstagramResearch !== true;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [source, setSource] = useState(null);
  const [changes, setChanges] = useState(null);
  const savedDataRef = useRef(null);

  const handle = extractHandle(contactData?.lead_instagram);
  const contactId = contactData?.id;

  const saveToFirestore = useCallback(async (posts, json, language) => {
    if (!userId || !contactId) return;
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);

      const ai = json.aiInsight;
      const m = json.metrics || {};
      const insightLanguage = languageFromIgSettings(language);

      const igBlock = buildIgResearchDescriptionBlock({
        ai,
        metrics: m,
        followerCount: json.user?.follower_count,
        language: insightLanguage,
      });

      const existingDesc = contactData?.lead_description || '';
      const descClean = existingDesc.replace(/\n?\[IG Research\].*$/s, '').trim();
      const newDesc = igBlock
        ? (descClean ? `${descClean}\n[IG Research] ${igBlock}` : `[IG Research] ${igBlock}`)
        : descClean;

      const savePayload = {
        'researchIntelligence.instagramInsight': ai || null,
        'researchIntelligence.instagramPosts': posts.map(p => ({
          text: (p.text || '').substring(0, 500),
          like_count: p.like_count || 0,
          comment_count: p.comment_count || 0,
          created_at: p.created_at || null,
          media_format: p.media_format || 'image',
          thumbnail_url: p.thumbnail_url || null,
        })),
        'researchIntelligence.instagramMeta': {
          ...m,
          handle,
          fullname: json.user?.fullname || null,
          fetchedAt: new Date().toISOString(),
        },
        lead_description: newDesc,
        lead_instagram: handle ? `https://instagram.com/${handle}` : '',
        ...(json.user?.follower_count != null ? { lead_instagram_followers: json.user.follower_count } : {}),
        ...(ai?.topics?.[0] ? { lead_instagram_category: ai.topics[0] } : {}),
      };
      await updateDoc(contactRef, savePayload);
      await recordCompanyRecord(userId, contactId, 'instagram', contactData, {
        ...contactData,
        researchIntelligence: {
          ...(contactData?.researchIntelligence || {}),
          instagramInsight: ai || null,
          instagramMeta: savePayload['researchIntelligence.instagramMeta'],
        },
        lead_instagram_followers: json.user?.follower_count ?? contactData?.lead_instagram_followers,
      });

      try {
        const freshData = { ...contactData, ...savePayload, lead_description: newDesc };
        computeAndStoreSignals(userId, contactId, freshData, { force: true }).catch(() => {});
      } catch (_) {}
    } catch (saveErr) {
      console.error('[InstagramResearch] Save failed:', saveErr);
    }
  }, [userId, contactId, handle, contactData]);

  const deductCredits = useCallback(async () => {
    if (!userId) throw new Error('Not authenticated');
    const deductCreditsFn = httpsCallable(functions, 'deductCredits');
    await deductCreditsFn({ action: 'instagram_research', amount: IG_RESEARCH_CREDIT_COST, worldId: userId, metadata: { handle, contactId } });
  }, [userId, handle, contactId]);

  const fetchFresh = useCallback(async () => {
    if (!handle) return;
    // Bereits vorhandene Ergebnisse bleiben sichtbar, ein neuer (kostenpflichtiger)
    // Lauf braucht aber die Lizenz — das Modal ist über das Zeilen-Badge auch
    // ohne Umweg über den Research-Dialog erreichbar.
    if (locked) {
      setError('Instagram Research is not available for your account.');
      return;
    }
    setLoading(true);
    setError(null);
    setChanges(null);

    try {
      await deductCredits();

      let insightLanguage = languageFromIgSettings(null);
      try {
        const langSnap = await get(ref(database, IG_RESEARCH_SETTINGS_PATH(userId)));
        insightLanguage = languageFromIgSettings(langSnap.val());
      } catch (_) { /* keep default */ }

      const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.instagramResearch(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handle, language: insightLanguage }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      const freshPosts = json.posts || [];

      const prev = savedDataRef.current;
      const detected = {};

      if (prev?.posts?.length) {
        const { merged, newCount } = mergePosts(prev.posts, freshPosts);
        json.posts = merged;
        detected.newPosts = newCount;

        const removedCount = (prev.posts || []).filter(
          op => !freshPosts.some(np => postFingerprint(np) === postFingerprint(op))
        ).length;
        if (removedCount > 0) detected.removedPosts = removedCount;
      }

      if (prev?.user?.fullname && json.user?.fullname && prev.user.fullname !== json.user.fullname) {
        detected.nameChanged = { from: prev.user.fullname, to: json.user.fullname };
      }

      setData(json);
      setSource('fresh');
      if (Object.keys(detected).length > 0) setChanges(detected);

      const postsToSave = (json.posts || []).filter(p => !p._isOld);
      await saveToFirestore(postsToSave.length > 0 ? postsToSave : freshPosts, json, insightLanguage);
      savedDataRef.current = json;
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Not enough credits') || msg.includes('resource-exhausted')) {
        setError('Not enough credits for Instagram Research (3 credits required). Please add more credits.');
      } else {
        setError(msg);
        // If profile not found, clear the invalid handle from the contact
        const lower = msg.toLowerCase();
        if (lower.includes('404') || lower.includes('not found') || lower.includes('user not found')) {
          try {
            const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
            await updateDoc(contactRef, { lead_instagram: '', has_instagram: false });
          } catch (_) {}
        }
      }
    } finally {
      setLoading(false);
    }
  }, [handle, locked, saveToFirestore, deductCredits, userId, contactId]);

  const loadSaved = useCallback(async () => {
    if (!userId || !contactId) return null;
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      const snap = await getDoc(contactRef);
      if (!snap.exists()) return null;
      const ri = snap.data()?.researchIntelligence;
      if (!ri?.instagramPosts?.length) return null;
      return {
        posts: ri.instagramPosts,
        metrics: ri.instagramMeta || {},
        user: ri.instagramMeta ? { fullname: ri.instagramMeta.fullname, username: ri.instagramMeta.handle } : null,
        aiInsight: ri.instagramInsight || null,
      };
    } catch (err) {
      console.error('[InstagramResearch] Load saved failed:', err);
      return null;
    }
  }, [userId, contactId]);

  useEffect(() => {
    if (!isOpen || !handle) return;
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setSource(null);
      setChanges(null);

      const saved = await loadSaved();
      if (cancelled) return;

      if (saved) {
        savedDataRef.current = saved;
        setData(saved);
        setSource('saved');
        setLoading(false);
      } else {
        savedDataRef.current = null;
        await fetchFresh();
      }
    };

    init();
    return () => { cancelled = true; };
  }, [isOpen, handle, loadSaved, fetchFresh]);

  if (!isOpen) return null;

  const user = data?.user;
  const posts = data?.posts || [];
  const metrics = data?.metrics || {};

  const avgLikes = metrics.avgLikes ?? (posts.length > 0 ? Math.round(posts.reduce((s, p) => s + (p.like_count || 0), 0) / posts.length) : 0);
  const avgComments = metrics.avgComments ?? (posts.length > 0 ? Math.round(posts.reduce((s, p) => s + (p.comment_count || 0), 0) / posts.length) : 0);

  const displayName = user?.fullname || contactData?.business_name || handle;
  const fetchedAt = metrics.fetchedAt ? new Date(metrics.fetchedAt) : null;
  const hasChanges = changes && (changes.newPosts > 0 || changes.removedPosts > 0 || changes.nameChanged);

  return (
    <div className="igr-overlay" onClick={onClose}>
      <div className="igr-modal" onClick={e => e.stopPropagation()}>

        <div className="igr-header">
          <div className="igr-header-left">
            <a
              href={`https://instagram.com/${handle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="igr-header-icon"
              title="Open profile on Instagram"
            >
              <i className="fab fa-instagram" />
            </a>
            <div className="igr-header-info">
              <h3 className="igr-title">
                <a
                  href={`https://instagram.com/${handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="igr-title-link"
                >
                  @{handle} <i className="fa fa-external-link-alt" />
                </a>
              </h3>
              {!loading && displayName !== handle && (
                <span className="igr-handle">{displayName}</span>
              )}
            </div>
          </div>
          <button className="igr-close" onClick={onClose}><i className="fa fa-times" /></button>
        </div>

        {loading && (
          <div className="igr-loading">
            <div className="igr-loading-icon">
              <i className="fab fa-instagram" />
              <div className="igr-loading-ring" />
            </div>
            <span className="igr-loading-text">Fetching posts & analyzing...</span>
          </div>
        )}

        {error && (
          <div className="igr-error">
            <i className="fa fa-exclamation-circle" />
            <span>{error}</span>
          </div>
        )}

        {data && !loading && (
          <div className="igr-body">

            <div className="igr-topbar">
              <div className="igr-topbar-left">
                {metrics.isActive !== undefined && (
                  <span className={`igr-activity-badge ${metrics.isActive ? 'igr-activity--active' : 'igr-activity--inactive'}`}>
                    <i className={`fa ${metrics.isActive ? 'fa-circle' : 'fa-moon'}`} />
                    {metrics.isActive
                      ? `Active${metrics.daysSinceLastPost != null ? ` · ${metrics.daysSinceLastPost === 0 ? 'today' : metrics.daysSinceLastPost + 'd ago'}` : ''}`
                      : `Inactive${metrics.daysSinceLastPost != null ? ` · ${metrics.daysSinceLastPost}d` : ''}`}
                  </span>
                )}
                {!metrics.isActive && metrics.postedInLast30Days && (
                  <span className="igr-activity-badge igr-activity--30d">
                    <i className="fa fa-calendar-check" />
                    Posted in last 30d
                  </span>
                )}
                {metrics.isDeadAccount && (
                  <span className="igr-activity-badge igr-activity--dead">
                    <i className="fa fa-skull-crossbones" />
                    Dead account
                  </span>
                )}
                {metrics.lastPostDate && (
                  <span className="igr-last-post">
                    <i className="fa fa-clock" />
                    Last post: {new Date(metrics.lastPostDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                )}
              </div>
              <div className="igr-stats-inline">
                {metrics.activityScore != null && (
                  <span className={`igr-score igr-score--${metrics.activityScore >= 7 ? 'high' : metrics.activityScore >= 4 ? 'mid' : 'low'}`} title="Activity Score">
                    <i className="fa fa-bolt" />{metrics.activityScore}
                  </span>
                )}
                {metrics.engagementScore != null && (
                  <span className={`igr-score igr-score--${metrics.engagementScore >= 7 ? 'high' : metrics.engagementScore >= 4 ? 'mid' : 'low'}`} title="Engagement Score">
                    <i className="fa fa-fire" />{metrics.engagementScore}
                  </span>
                )}
                <span className="igr-stat-inline"><i className="fa fa-heart" />{formatNumber(avgLikes)}</span>
                <span className="igr-stat-inline"><i className="fa fa-comment" />{formatNumber(avgComments)}</span>
                <span className="igr-stat-inline"><i className="fa fa-images" />{posts.length}</span>
                {metrics.avgPostsPerWeek != null && (
                  <span className="igr-stat-inline"><i className="fa fa-calendar-alt" />{metrics.avgPostsPerWeek}/w</span>
                )}
                {metrics.engagementTrend && metrics.engagementTrend !== 'stable' && (
                  <span className={`igr-trend igr-trend--${metrics.engagementTrend}`}>
                    <i className={`fa fa-arrow-${metrics.engagementTrend === 'growing' ? 'up' : 'down'}`} />
                  </span>
                )}
              </div>
            </div>

            {source === 'saved' && (
              <div className="igr-source-bar">
                <span className="igr-source-text">
                  <i className="fa fa-database" />
                  Saved{fetchedAt ? ` · ${fetchedAt.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })}` : ''}
                </span>
                <button className="igr-refresh-btn" onClick={fetchFresh} disabled={loading}>
                  <i className="fa fa-sync-alt" />
                  Check for new posts
                </button>
              </div>
            )}

            {source === 'fresh' && hasChanges && (
              <div className="igr-changes-bar">
                {changes.newPosts > 0 && (
                  <span className="igr-change igr-change--new">
                    <i className="fa fa-plus-circle" />
                    {changes.newPosts} new post{changes.newPosts !== 1 ? 's' : ''}
                  </span>
                )}
                {changes.newPosts === 0 && (
                  <span className="igr-change igr-change--none">
                    <i className="fa fa-minus-circle" />
                    No new posts since last check
                  </span>
                )}
                {changes.removedPosts > 0 && (
                  <span className="igr-change igr-change--removed">
                    <i className="fa fa-trash-alt" />
                    {changes.removedPosts} post{changes.removedPosts !== 1 ? 's' : ''} deleted
                  </span>
                )}
                {changes.nameChanged && (
                  <span className="igr-change igr-change--name">
                    <i className="fa fa-user-edit" />
                    Name: {changes.nameChanged.from} → {changes.nameChanged.to}
                  </span>
                )}
              </div>
            )}

            {source === 'fresh' && !hasChanges && !changes && (
              <div className="igr-saved-hint">
                <i className="fa fa-check-circle" />
                <span>Posts & AI insights saved</span>
              </div>
            )}

            <div className="igr-posts">
              {posts.map((post, i) => (
                <div key={i} className={`igr-post ${!post.thumbnail_url ? 'igr-post--no-thumb' : ''} ${post._isNew ? 'igr-post--new' : ''} ${post._isOld ? 'igr-post--old' : ''}`}>
                  {post._isNew && <span className="igr-post-new-badge">NEW</span>}
                  {post._isOld && <span className="igr-post-old-badge">DELETED</span>}
                  {post.thumbnail_url && (
                    <img src={post.thumbnail_url} alt="" className="igr-post-thumb" />
                  )}
                  <div className="igr-post-content">
                    <p className="igr-post-text">
                      {post.text?.length > 180 ? post.text.slice(0, 180) + '...' : post.text}
                    </p>
                    <div className="igr-post-meta">
                      <span className="igr-post-stat">
                        <i className="fa fa-heart" />{formatNumber(post.like_count)}
                      </span>
                      <span className="igr-post-stat">
                        <i className="fa fa-comment" />{formatNumber(post.comment_count)}
                      </span>
                      <span className="igr-post-date">{timeAgo(post.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}

              {posts.length === 0 && (
                <div className="igr-empty">
                  <i className="fab fa-instagram" />
                  <span>No posts found</span>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default InstagramResearchModal;
